/* eslint-disable camelcase */

import React from 'react';

import styled from '@emotion/styled';
import { Button } from '@lightspeed/flame/Button';
import { Checkbox } from '@lightspeed/flame/Checkbox';
import { Box, Flex } from '@lightspeed/flame/Core';
import { IconChevronDown } from '@lightspeed/flame/Icon/ChevronDown';
import { IconChevronRight } from '@lightspeed/flame/Icon/ChevronRight';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalProps } from '@lightspeed/flame/Modal';
import { Spinner } from '@lightspeed/flame/Spinner';
import { Text, TextLink } from '@lightspeed/flame/Text';

import { Eventbus } from '../app/src/data/Eventbus';
import { createReactRenderer } from '../helpers/react-renderer';
import { translate } from '../pages/utils/translate';
import Modules from '../shared_modules/Modules';
import SDK, { LightspeedPayments } from '@lightspeed/online-payments-sdk';
import { trackAnalyticsEvent } from 'helpers/AnalyticsTracking';

const StyledModal = styled(Modal)`
  width: 600px;
  max-width: 90vw;
  max-height: 90vh;
`;

const StyledModalBody = styled(ModalBody)`
  position: relative;
`;

const StyledBoxText = styled(Text)`
  font-style: italic;
  font-size: 12px;
  font-weight: lighter;
`;

const LoadingOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  z-index: 10;
`;

interface CustomerWidgetProps {
  customerId: string;
  onLoad: () => void;
  onLoadError: () => void;
  onWidgetSuccess: () => void;
  onWidgetFailure: (event: SDK.Event) => void;
}

/**
 * The SDK that mounts the Lightspeed Payments widget interacts poorly with react state refreshes.
 * So it must be memoized. Because it's separate from the modal, it's built in submit method needs
 * to be triggered by the event bus.
 * */

const CreditWidget = React.memo(function ({
  customerId,
  onLoad,
  onLoadError,
  onWidgetSuccess,
  onWidgetFailure,
}: CustomerWidgetProps) {
  const widgetMountRef = React.useRef<HTMLDivElement>(null);
  const widgetControllerRef = React.useRef<SDK.WidgetController | null>(null);

  const mountWidget = React.useCallback(() => {
    fetch(`/admin/customers/${customerId}/cards/session`).then((response) => {
      response.json().then(async ({ session_id }) => {
        try {
          widgetControllerRef.current = await LightspeedPayments.v1.mountPaymentWidget(session_id, {
            mountPoint: widgetMountRef.current!,
            listeners: {
              onReady: onLoad,
              onSucceeded: onWidgetSuccess,
              onError: onWidgetFailure,
              onDeclined: onWidgetFailure,
            },
          });
        } catch (error) {
          onLoadError();
        }
      });
    });
  }, [customerId, onLoad, onLoadError, onWidgetFailure, onWidgetSuccess]);

  const submitCard = React.useCallback(() => {
    widgetControllerRef.current?.submit();
  }, []);

  React.useEffect(() => {
    if (widgetMountRef.current) {
      mountWidget();
    }

    Eventbus.listen({
      eventName: 'add_card:clicked',
      callbackFn: submitCard,
    });

    return () => {
      widgetControllerRef.current?.unmount();
      Eventbus.unSubscribe('add_card:clicked', submitCard);
    };
  }, [mountWidget, submitCard]);

  return <Box textAlign="center" ref={widgetMountRef} />;
});

type AddSavedCardModalProps = ModalProps & {
  customerId: string;
  onComplete: () => void;
};

export default function AddSavedCardModal({
  customerId,
  onComplete,
  onRequestClose,
  ...modalProps
}: AddSavedCardModalProps) {
  const [isConfirmed, setIsConfirmed] = React.useState(false);
  const [showDetails, setShowDetails] = React.useState(false);
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleClose = () => {
    if (isInitialLoading || isSubmitting) return;
    onRequestClose?.(new MouseEvent('click'));
  };

  const handleAddClick = () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    Eventbus.emit('add_card:clicked');
  };

  const handleLoad = React.useCallback(() => {
    setIsInitialLoading(false);
  }, []);

  const handleLoadError = React.useCallback(() => {
    setIsInitialLoading(false);
    window.alertUser(
      translate('There was an error loading the card input. Please try again later.')
    );
    onRequestClose?.(new MouseEvent('click'));
  }, [onRequestClose]);

  const handleSuccess = React.useCallback(() => {
    window.flashNotifier('success', translate('Card added successfully'), 3);
    // The Success/Complete is the status returned from the psp (Stripe).
    // So the payment method has been created and attached to a customer in stripe,
    // but it will take a second for the asynchronous events that stripe will generate
    // to be handled by the processing service so the actual db records get created

    // set a 5 second timeout to allow the async events to be processed
    setTimeout(() => {
      if (Modules.hasModule('analytics.card_on_file')) {
        trackAnalyticsEvent({
          eventCategory: 'cof',
          eventName: 'add',
          eventAction: 'add_via_customer_panel',
          eventLabel: 'success',
        });
      }

      // Refetch the saved cards to ensure the new card is displayed
      setIsSubmitting(false);
      onComplete();
    }, 5000);
  }, [onComplete]);

  const handleFailure = React.useCallback((event: SDK.Event) => {
    setIsSubmitting(false);

    const errorMessages: Record<string, string> = {
      'Declined.PaymentMethodNotSupported':
        'Card type is not supported. Please use a different card.',
      'Declined.CardValidation':
        'Please complete all required fields and ensure your card details are correct.',
      'Error.FormValidation':
        'Please complete all required fields and ensure your card details are correct.',
    };

    const defaultMessage =
      'An error occurred while processing the card. Try again later or with a different payment method.';

    const messageKey = `${event.status}.${event.code}`;
    setErrorMessage(errorMessages[messageKey] || defaultMessage);

    if (Modules.hasModule('analytics.card_on_file')) {
      trackAnalyticsEvent({
        eventCategory: 'cof',
        eventName: 'af',
        eventAction: 'add_via_customer_panel',
        eventLabel: 'error',
      });
    }
  }, []);

  const handleMoreDetailsClick = React.useCallback(() => {
    setShowDetails((previous) => !previous);
  }, []);

  return (
    <StyledModal
      {...modalProps}
      isOpen={true}
      onRequestClose={handleClose}
      closeOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      <ModalHeader>Add a card</ModalHeader>
      <StyledModalBody scroll={true}>
        <CreditWidget
          customerId={customerId}
          onLoad={handleLoad}
          onLoadError={handleLoadError}
          onWidgetSuccess={handleSuccess}
          onWidgetFailure={handleFailure}
        />
        {isSubmitting && (
          <LoadingOverlay>
            <Spinner data-test="submitting-spinner" size="48px" color="#3d89d1" />
          </LoadingOverlay>
        )}
        <Box mt="24px">
          {errorMessage && (
            <Text color="red" mb="16px">
              {errorMessage}
            </Text>
          )}
          <Checkbox
            data-test="consent-checkbox"
            defaultChecked={false}
            onChange={(e) => setIsConfirmed(e.target.checked)}
            label={
              <Box>
                <Text fontWeight="normal">
                  Merchant hereby confirms that it has requested and received explicit cardholder
                  consent to store card and cardholder information in this system.
                </Text>
              </Box>
            }
          />
          <Box ml="28px">
            <Box mt="8px">
              <TextLink onClick={handleMoreDetailsClick}>
                {showDetails ? 'Less terms & conditions' : 'Additional terms & conditions'}{' '}
                {showDetails ? (
                  <IconChevronDown color="#2E61DE" />
                ) : (
                  <IconChevronRight color="#2E61DE" />
                )}
              </TextLink>
            </Box>
            <Box mt="8px">
              {showDetails && (
                <StyledBoxText>
                  Lightspeed will not be liable to merchant or cardholder for any loss whatsoever
                  including but not limited to losses related to (i) card and cardholder information
                  stored without cardholder consent (ii) any charges made to this card by merchant
                  with or without customer authorization (iii) insufficient card or cardholder
                  verifications by merchant.
                </StyledBoxText>
              )}
            </Box>
          </Box>
        </Box>
      </StyledModalBody>
      <ModalFooter>
        <Flex justifyContent="flex-end">
          <Button onClick={handleClose} mr="12px">
            Cancel
          </Button>
          <Button
            aria-busy={isSubmitting}
            loading={isSubmitting}
            variant="secondary"
            fill={true}
            onClick={handleAddClick}
            disabled={!isConfirmed || isInitialLoading || isSubmitting}
            data-test="add-card-button"
          >
            Add card and save changes
          </Button>
        </Flex>
      </ModalFooter>
    </StyledModal>
  );
}

// Function to open the AddSavedCardModal from anywhere in the application
export const openAddSavedCardModal = ({
  customerId,
  onComplete,
}: {
  customerId: string;
  onComplete: () => void;
}) => {
  const RenderedComponent = createReactRenderer({
    component: AddSavedCardModal,
    selector: '[data-react-root="ReactModal"]',
  });
  const onRequestClose = () => {
    RenderedComponent.unmount();
  };
  RenderedComponent.mount({ props: { customerId, onComplete, onRequestClose } });
};
